import { useState } from "react";
import {
  EditOutlined,
  DeleteOutlined,
  LeftCircleOutlined,
  LoadingOutlined,
  PlusCircleOutlined,
  EyeOutlined
} from "@ant-design/icons";
import { Button, Col, Descriptions, Row, Table, Tag, notification, Typography, Space, Popconfirm } from "antd";
import { ColumnsType } from "antd/es/table";
import {
  useGetBrandQuery,
  useUpdateModelMutation,
  useCreateModelMutation,
  useCreateChargerTypeMutation,
  useUpdateChargerTypeMutation
} from "../../redux/services/brandApi";
import {
  useGetDealerMappingsQuery,
  useUpdateDealerMappingMutation,
  useDeleteDealerMappingMutation,
  useCreateDealerMappingMutation
} from "../../redux/services/dealerMappingApi";
import AddModelInBrandModal from "./AddModelInBrandModal";
import AddChargerTypeInBrandModal from "./AddChargerTypeInBrandModal";
import AddDealerMappingInBrandModal from "../DealerMappings/AddDealerMappingInBrandModal";
import DealerMappingTable from "../DealerMappings/DealerMappingTable";
import ErrorDetails from "../../components/ErrorDetails";
import { useNavigate, useParams, Link } from "react-router-dom";
import { chargerTypeOptions } from "../../constants/helperConstants";

const { Text } = Typography;
const ViewBrand = (props: any) => {
  const params = useParams();
  const navigate = useNavigate();

  const brandId = params?.brandId;

  const [api, contextHolder] = notification.useNotification();
  const [showModal, setShowModal] = useState(false);
  const [chargerTypeShowModal, setChargerTypeShowModal] = useState(false);
  const [dealerMappingShowModal, setDealerMappingShowModal] = useState(false);
  const [selectedModel, setSelectedModel] = useState({});
  const [selectedChargerType, setSelectedChargerType] = useState({});
  const [selectedDealerMapping, setSelectedDealerMapping] = useState({});
  const [isEditMode, setEditMode] = useState(false);
  const { data, error, isLoading, refetch } = useGetBrandQuery(brandId);
  const brand = data?.brand;

  const [dealerMappingQueryParams, setDealerMappingQueryParams] = useState<any>({
    brand_id: brandId,
    include_all: true,
    page: 1
  });

  const {
    data: dealerMappingsData,
    refetch: refetchDealerMappings,
    isFetching
  } = useGetDealerMappingsQuery({
    ...dealerMappingQueryParams
  });

  const [createModel] = useCreateModelMutation();

  const [updateModel] = useUpdateModelMutation();

  const [createDealerMapping] = useCreateDealerMappingMutation();

  const [updateDealerMapping] = useUpdateDealerMappingMutation();

  const [deleteDealerMapping] = useDeleteDealerMappingMutation();

  const [createChargerType] = useCreateChargerTypeMutation();

  const [updateChargerType] = useUpdateChargerTypeMutation();


  const onGoBack = () => {
    navigate(-1);
  };

  const onUpdateModel = async (values: object, setLoading: any, form: any) => {
    try {
      setLoading(true);
      await updateModel(values).unwrap();
      api.success({
        message: "Update model successful",
        placement: "top"
      });
      form.resetFields();
      setLoading(false);
      setShowModal(false);
      setEditMode(false);

      refetch();
    } catch (error: any) {
      api.info({
        message: "Update model failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const onUpdateChargerType = async (values: object, setLoading: any, form: any) => {
    try {
      setLoading(true);
      await updateChargerType(values).unwrap();
      api.success({
        message: "Update charger type successful",
        placement: "top"
      });
      form.resetFields();
      setLoading(false);
      setChargerTypeShowModal(false);
      setEditMode(false);

      refetch();
    } catch (error: any) {
      api.info({
        message: "Update charger type failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const onUpdateDealerMapping = async (values: object, setLoading: any, form: any) => {
    try {
      setLoading(true);
      await updateDealerMapping(values).unwrap();
      api.success({
        message: "Update dealer mapping successful",
        placement: "top"
      });
      form.resetFields();
      setLoading(false);
      setDealerMappingShowModal(false);
      setEditMode(false);

      refetchDealerMappings();
    } catch (error: any) {
      api.info({
        message: "Update dealer mapping failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const onDeleteDealerMapping = async (dealer_mapping_id: any) => {
    try {
      const values = { id: dealer_mapping_id };
      await deleteDealerMapping(values).unwrap();
      api.success({
        message: "Delete dealer mapping successful",
        placement: "top"
      });
      setDealerMappingShowModal(false);
      setEditMode(false);

      refetchDealerMappings();
    } catch (error: any) {
      api.info({
        message: "Delete dealer mapping operartion failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
    }
  };

  const onUpdate = (model: any) => {
    setSelectedModel(model);
    setShowModal(true);
    setEditMode(true);
  };

  const onEditChargerType = (charger_type: any) => {
    setSelectedChargerType(charger_type);
    setChargerTypeShowModal(true);
    setEditMode(true);
  };

  const onEditDealerMapping = (dealer_mapping: any) => {
    setSelectedDealerMapping(dealer_mapping);
    setDealerMappingShowModal(true);
    setEditMode(true);
  };

  const columns: ColumnsType<any> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name"
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code"
    },
    {
      title: "Charger type",
      dataIndex: "charger_size",
      key: "charger_size",
      render: (charger_size) => <Text key={charger_size}>{chargerTypeOptions.filter((x) => x.value === charger_size)[0]?.label}</Text>
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      render: (tag) => (
        <Tag color={tag === "active" ? "green" : "red"} key={tag}>
          {tag.toUpperCase()}
        </Tag>
      )
    },
    {
      title: "Action",
      key: "action",
      render: (_, model) => (
        <Space size="middle" direction="horizontal">
          <>
            <Link to={`/models/${model?.id}`}>
              <Button icon={<EyeOutlined />} title="View"></Button>
            </Link>
            <Button icon={<EditOutlined />} onClick={() => onUpdate(model)} title="Update"></Button>
          </>
        </Space>
      )
    }
  ];

  const chargerTypeColumns: ColumnsType<any> = [
    {
      title: "Charger type",
      dataIndex: "charger_size",
      key: "charger_size",
      render: (charger_size) => <Text key={charger_size}>{chargerTypeOptions.filter((x) => x.value === charger_size)[0]?.label}</Text>
    },
    {
      title: "Action",
      key: "action",
      render: (_, charger_size) => (
        <Space size="middle" direction="horizontal">
          <>
            <Button icon={<EditOutlined />} onClick={() => onEditChargerType(charger_size)} title="Update"></Button>
          </>
        </Space>
      )
    }
  ];

  const addModel = async (values: object, setLoading: any, form: any) => {
    try {
      setLoading(true);
      await createModel(values).unwrap();
      api.success({
        message: "Create model successful",
        placement: "top"
      });
      form.resetFields();
      setLoading(false);
      setShowModal(false);
      refetch();
    } catch (error: any) {
      api.info({
        message: "Create model failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const addDealerMapping = async (values: object, setLoading: any, form: any) => {
    try {
      setLoading(true);
      await createDealerMapping(values).unwrap();
      api.success({
        message: "Create dealer mapping successful",
        placement: "top"
      });
      form.resetFields();
      setLoading(false);
      setDealerMappingShowModal(false);
      refetchDealerMappings();
    } catch (error: any) {
      api.info({
        message: "Create dealer mapping failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const addChargerType = async (values: object, setLoading: any, form: any) => {
    try {
      setLoading(true);
      await createChargerType(values).unwrap();
      api.success({
        message: "Create charger type successful",
        placement: "top"
      });
      form.resetFields();
      setLoading(false);
      setChargerTypeShowModal(false);
      refetch();
    } catch (error: any) {
      api.info({
        message: "Create charger type failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  return (
    <>
      {contextHolder}
      {!isLoading && brand && (
        <>
          <Row style={{ marginBottom: 20 }}>
            <Col
              sm={24}
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <h2>Brand Details</h2>
              <Button onClick={onGoBack} size="middle" type="default" icon={<LeftCircleOutlined />}>
                Back
              </Button>
            </Col>
          </Row>

          {brand?.brand_logo ? (
            <div style={{ display: "flex", gap: 20, alignItems: "center", marginBottom: 20 }}>
              <div style={{ height: "50px" }}>
                {brand?.brand_logo ? (
                  <img src={brand?.brand_logo} alt="no-img" height="50px" />
                ) : (
                  "-"
                )}
              </div>
              <h2>
                {brand?.name} ({brand?.code})
              </h2>
            </div>
          ) : (
            ""
          )}

          <Descriptions
            title=""
            bordered
            column={1}
            labelStyle={{ fontWeight: 700, width: 300 }}
            size="middle"
          >
            <Descriptions.Item label="Name">{brand?.name}</Descriptions.Item>
            <Descriptions.Item label="Code">{brand?.code || "-"}</Descriptions.Item>
            <Descriptions.Item label="Contact Person">
              {brand?.contact_person || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Contact Number">
              {brand?.contact_number || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Another Contact Number">
              {brand?.alt_contact_number || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="GST">{brand?.gst || "-"}</Descriptions.Item>
            <Descriptions.Item label="Billing Address">
              {brand?.billing_address || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={brand?.status === "active" ? "green" : "red"}>
                {brand?.status.toUpperCase()}
              </Tag>
            </Descriptions.Item>
            {/* <Descriptions.Item label="Models">
              {brand?.models?.map((model: any) => (
                <div key={model?.name}>
                  <Text>
                    {model?.name.toUpperCase()} - {model?.code} - {model?.charger_size}
                  </Text>{" "}
                  <Tag color={model?.status === "active" ? "green" : "red"} key={model?.status}>
                    {model?.status?.toUpperCase()}
                  </Tag>
                </div>
              ))}
            </Descriptions.Item> */}
          </Descriptions>

          <Row style={{ marginTop: 20, marginBottom: 20 }}>
            <Col
              sm={24}
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <h2>Models</h2>
              <Button
                size="middle"
                onClick={() => setShowModal(!showModal)}
                icon={<PlusCircleOutlined />}
                type="default"
              >
                Add Models
              </Button>
            </Col>
          </Row>

          <Table
            rowKey="id"
            bordered
            columns={columns}
            pagination={false}
            dataSource={brand?.models}
            scroll={{ x: 400 }}
          />

          <AddModelInBrandModal
            isModalOpen={showModal}
            brand={brand}
            onCancel={() => {
              setShowModal(false);
              setEditMode(false);
            }}
            onUpdateModel={isEditMode && onUpdateModel}
            isEditMode={isEditMode}
            selectedModel={isEditMode ? selectedModel : {}}
            addModel={!isEditMode && addModel}
          />

          <Row style={{ marginTop: 20, marginBottom: 20 }}>
            <Col
              sm={24}
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <h2>Charger Types</h2>
              <Button
                size="middle"
                onClick={() => setChargerTypeShowModal(!chargerTypeShowModal)}
                icon={<PlusCircleOutlined />}
                type="default"
              >
                Add Charger Types
              </Button>
            </Col>
          </Row>

          <Table
            rowKey="id"
            bordered
            columns={chargerTypeColumns}
            pagination={false}
            dataSource={brand?.charger_types}
            scroll={{ x: 400 }}
          />

          <AddChargerTypeInBrandModal
            isModalOpen={chargerTypeShowModal}
            brand={brand}
            onCancel={() => {
              setChargerTypeShowModal(false);
              setEditMode(false);
            }}
            onUpdateChargerType={isEditMode && onUpdateChargerType}
            isEditMode={isEditMode}
            selectedChargerType={isEditMode ? selectedChargerType : {}}
            addChargerType={!isEditMode && addChargerType}
          />

          <Row style={{ marginTop: 20, marginBottom: 20 }}>
            <Col
              sm={24}
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <h2>Dealer Mappings</h2>
              <Button
                size="middle"
                onClick={() => setDealerMappingShowModal(!dealerMappingShowModal)}
                icon={<PlusCircleOutlined />}
                type="default"
              >
                Add Dealer Mappings
              </Button>
            </Col>
          </Row>

          {brand && (
            <DealerMappingTable
              brand={brand}
              dealerMappingQueryParams={dealerMappingQueryParams}
              setDealerMappingQueryParams={setDealerMappingQueryParams}
              dealerMappingsData={dealerMappingsData}
              isFetching={isFetching}
              onDeleteDealerMapping={onDeleteDealerMapping}
              onEditDealerMapping={onEditDealerMapping}
            />
          )}

          <AddDealerMappingInBrandModal
            isModalOpen={dealerMappingShowModal}
            brand={brand}
            onCancel={() => {
              setDealerMappingShowModal(false);
              setEditMode(false);
            }}
            onUpdateDealerMapping={isEditMode && onUpdateDealerMapping}
            isEditMode={isEditMode}
            selectedDealerMapping={isEditMode ? selectedDealerMapping : {}}
            addDealerMapping={!isEditMode && addDealerMapping}
          />
        </>
      )}
      {isLoading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh"
          }}
        >
          <LoadingOutlined style={{ fontSize: 24 }} spin />
        </div>
      )}
    </>
  );
};

export default ViewBrand;
